import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { MIN_PLAYER_QUERY, usePlayers } from '../api/hooks';
import type { AdminRole } from '../api/types';
import { Avatar, Badge, Icon, type IconName } from '../ui';
import { USER_STATUS } from './labels';
import type { NavItem } from './Shell';
import s from './Shell.module.css';

interface Page {
  to: string;
  label: string;
  icon: IconName;
}

export const NAV_PAGES = (nav: Array<{ group: string; items: NavItem[] }>, can: (r?: AdminRole) => boolean): Page[] => nav.flatMap((g) => g.items.filter((i) => can(i.role)));

/**
 * ⌘K: type an email (or its start), a player ID, a name or a user id and Enter opens that player;
 * page names jump to the page. Arrow keys move, Escape closes.
 */
export function CommandPalette({ open, onClose, pages, canSearchPlayers }: { open: boolean; onClose: () => void; pages: Page[]; canSearchPlayers: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [sel, setSel] = useState(0);
  const navigate = useNavigate();
  const players = usePlayers(canSearchPlayers ? debounced : '');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      setQ('');
      setDebounced('');
      setSel(0);
      el.showModal();
    }
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  const term = q.trim().toLowerCase();
  const pageHits = useMemo(() => (term ? pages.filter((p) => p.label.toLowerCase().includes(term)) : pages.slice(0, 6)), [pages, term]);
  const playerHits = debounced.length >= MIN_PLAYER_QUERY ? (players.data ?? []).slice(0, 8) : [];
  const items = [...playerHits.map((p) => ({ kind: 'player' as const, key: p._id, to: `/users/${p._id}`, p })), ...pageHits.map((p) => ({ kind: 'page' as const, key: p.to, to: p.to, p }))];

  useEffect(() => {
    setSel(0);
  }, [debounced, term]);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <dialog
      ref={ref}
      className={s.palette}
      aria-label="搜尋"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      {open && (
        <>
          <div className={s.paletteInput}>
            <Icon name="search" size={20} />
            <input
              autoFocus
              type="text"
              value={q}
              placeholder={canSearchPlayers ? '電郵、玩家 ID、名字或 user ID；或頁面名稱' : '頁面名稱'}
              aria-label="搜尋玩家或頁面"
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setSel((v) => Math.min(items.length - 1, v + 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setSel((v) => Math.max(0, v - 1));
                } else if (e.key === 'Enter' && items[sel]) {
                  e.preventDefault();
                  go(items[sel].to);
                }
              }}
            />
            {players.isFetching && <span className="small faint">搜尋中…</span>}
          </div>
          <div className={s.paletteList} role="listbox">
            {playerHits.length > 0 && <div className={s.paletteGroup}>玩家</div>}
            {items.map((it, i) =>
              it.kind === 'player' ? (
                <button key={it.key} type="button" role="option" aria-selected={i === sel} className={s.paletteItem} onMouseEnter={() => setSel(i)} onClick={() => go(it.to)}>
                  <Avatar name={it.p.characterName ?? it.p.email ?? '?'} size={32} />
                  <span className={s.paletteMain}>
                    <strong>{it.p.characterName ?? it.p.displayName ?? '（未建立角色）'}</strong>
                    <span>
                      {it.p.email ?? '訪客'} · {it.p.publicId}
                    </span>
                  </span>
                  <Badge tone={USER_STATUS[it.p.status].tone}>{USER_STATUS[it.p.status].label}</Badge>
                </button>
              ) : (
                <div key={it.key} style={{ display: 'contents' }}>
                  {(i === playerHits.length) && <div className={s.paletteGroup}>頁面</div>}
                  <button type="button" role="option" aria-selected={i === sel} className={s.paletteItem} onMouseEnter={() => setSel(i)} onClick={() => go(it.to)}>
                    <Icon name={it.p.icon} size={18} />
                    <span className={s.paletteMain}>
                      <strong>{it.p.label}</strong>
                    </span>
                  </button>
                </div>
              ),
            )}
            {debounced.length >= MIN_PLAYER_QUERY && canSearchPlayers && !players.isFetching && playerHits.length === 0 && <div className={s.paletteGroup}>找不到符合「{debounced}」的玩家</div>}
          </div>
          <div className={s.paletteHint}>
            <span>↑↓ 選擇</span>
            <span>Enter 打開</span>
            <span>Esc 關閉</span>
          </div>
        </>
      )}
    </dialog>
  );
}
